<template>
  <div class="trip-launched">
    <ConfettiAnimation v-if="isAnimating" />
    <div class="trip-launched__content">
      <h1 class="trip-launched__title">Your itinerary has launched!</h1>
      <span class="trip-launched__message">
        Congratulations! Your itinerary is live and ready to book. It will be discoverable on TourHero within 48 hours.
        You can start sharing your link right away.
      </span>
      <div class="product-card-container">
        <ProductCard v-if="product" :product="product" />
      </div>
      <Button
        v-if="product?.meta?.link"
        class="trip-launched__button"
        color="red"
        @click="redirectTo(product.meta.link)"
      >
        Go to itinerary
      </Button>
      <span class="trip-launched__contact_us">
        Have some more questions? You can
        <a :href="`/${locale}/travelers/messages?itineraryRequestId=${product?.itineraryRequestId}`">message us here</a
        >.</span
      >
    </div>
    <MarketingHubBanner v-if="showBanner" @close="showBanner = false" @cta-click="onMarketingHubClick" />
  </div>
</template>

<script setup lang="ts">
import _ from 'lodash'
import { onMounted, ref, watch } from 'vue'
import ConfettiAnimation from './ConfettiAnimation.vue'
import MarketingHubBanner from './components/MarketingHubBanner.vue'
import ProductCard from '@/modules/Explore/ProductCard2.vue'
import api from '@/utils/api'
import { useRoute } from 'vue-router'
import { deserializeJsonApi } from '../../utils/jsonApiDeserializer'
import Button from '../../components/Theme2/Button.vue'
import { redirectTo } from '../../utils/functions'
import { openMarketingHub } from '../../utils/generalUtils'
import { useShared } from '../../helpers/vueHelpers'

interface TripLaunchedProductCard {
  id: string
  meta: { link: string }
  itineraryRequestId: string
  itinerary: {
    baseCurrency: string
    durationNights: number
    title: string
    featureImageUrl: string
    heroImageUrl: string
    targetCountryName: string
    mbaExclusive: boolean
    id: string
  }
  event: {
    startsAt: string
    timezone: string
    lowestAmountCents: number
    spotsLeft: number
    id: string
  }
}

const isAnimating = ref(false)
const showBanner = ref(true)

function onMarketingHubClick() {
  openMarketingHub('launch_success', product.value?.meta?.link)
}
const route = useRoute()
const product = ref<TripLaunchedProductCard | null>(null)
const { locale } = useShared()

onMounted(async () => {
  const productId =
    route.params.id.includes('-') && typeof route.params.id === 'string'
      ? route.params.id.split('-')[route.params.id.split('-').length - 1]
      : route.params.id

  try {
    const response = await api.get(`/api/hero/products/${productId}/published`)
    const result = await deserializeJsonApi(response.data)
    product.value = {
      ..._.omit(result, 'displayableEvent'),
      event: result.displayableEvent,
    } as TripLaunchedProductCard
  } catch (error) {
    console.error(error)
    alert('Failed to get product')
  }
})

watch(
  () => product.value,
  () => {
    isAnimating.value = true

    setTimeout(() => {
      isAnimating.value = false
    }, 10000)
  },
)
</script>

<style lang="scss" scoped>
.trip-launched {
  @include main-unwrap;
  @include flex-column;
  position: relative;
  align-items: center;
  padding: $spacing-6;
  background: linear-gradient(135deg, $green-50 0%, $white 100%);

  @include mobile {
    justify-content: flex-start;
    min-height: 80vh;
    margin-bottom: calc(var(--site-px) * -1 - 16px);
  }

  @include desktop {
    min-height: 80vh;
    margin-bottom: calc(var(--site-px) * -1);
    justify-content: center;
  }

  @include xxl {
    margin-bottom: calc(var(--site-px) * -1 + 24px);
  }

  .trip-launched__content {
    @include flex-column($spacing-6);
    align-items: center;
    text-align: center;
    max-width: 800px;
    animation: fadeInUp 0.6s ease-out;

    @include xxl {
      gap: $spacing-10;
    }

    .trip-launched__title {
      font-size: $text-2xl;
      font-weight: $font-semibold;
      color: $green-500;
      margin: 0;

      @include mobile {
        font-size: $text-2xl;
      }
    }

    .trip-launched__message {
      @include mobile {
        font-size: $text-base;
      }
    }

    .product-card-container {
      text-align: left;
      max-width: 280px;

      @include xxl {
        max-width: 400px;
      }
    }

    .trip-launched__contact_us {
      a {
        color: $green-300;
      }
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
